import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { CITIES } from "../data/cities";
import { LOCATIONS } from "../data/locations";
import { SERVICES } from "../data/services";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CTABanner } from "../components/sections/CTABanner";
import { VendorMarquee } from "../components/sections/VendorStack";
import { SITE_URL } from "../data/site";

export default function CityPage() {
  const { slug, city: citySlug } = useParams();
  const loc = CITIES.find((c) => c.slug === citySlug && c.stateSlug === slug);
  if (!loc) return <Navigate to="/" replace />;

  const path = `/locations/${loc.stateSlug}/${loc.slug}`;
  const faqs = [
    ...(loc.faqs || []),
    {
      q: `Do you provide on-site IT support in ${loc.city}?`,
      a: `Yes. ${loc.city} is in our New England home region, so full service — including on-site visits for projects, planned work and emergencies — is included in the standard plan, not billed as an extra.`,
    },
    {
      q: `What kinds of ${loc.city} businesses do you work with?`,
      a: "Solo professionals, micro businesses and established firms of up to 25 employees — attorneys, accountants, healthcare practices, manufacturers and consultants. We deliberately don't pursue larger organizations; small business is our entire focus.",
    },
    {
      q: "Is everything really included in one plan?",
      a: "Yes. Every client gets the same full stack — managed IT, Huntress-powered cybersecurity, Microsoft 365 administration, 24/7 help desk with unlimited support hours, and immutable backup — under The Sempresera Standard.",
    },
  ];
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Managed IT Services",
    name: `Sempresera — Managed IT Services in ${loc.city}, ${loc.stateAbbr}`,
    description: loc.metaDescription,
    url: `${SITE_URL}${path}`,
    provider: { "@type": "Organization", name: "Sempresera", url: `${SITE_URL}/`, foundingDate: "2015" },
    areaServed: { "@type": "City", name: loc.city, containedInPlace: { "@type": "State", name: loc.state } },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: loc.state, item: `${SITE_URL}/locations/${loc.stateSlug}` },
      { "@type": "ListItem", position: 3, name: loc.city, item: `${SITE_URL}${path}` },
    ],
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const statePage = LOCATIONS.find((l) => l.slug === loc.stateSlug);

  return (
    <>
      <Seo title={loc.metaTitle} description={loc.metaDescription} path={path} jsonLd={[serviceLd, breadcrumbLd, faqLd]} />

      <section className="relative overflow-hidden" data-testid="city-hero">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 pb-20 pt-36 md:grid-cols-12 md:px-8 md:pt-44">
          <div className="md:col-span-7">
            <MaskedLine delay={0.1}>
              <span className="eyebrow">
                <Link to={`/locations/${loc.stateSlug}`} className="transition-colors duration-200 hover:text-white" data-testid="city-state-link">
                  {loc.state}
                </Link>
                {" · "}Sempresera
              </span>
            </MaskedLine>
            <h1 className="mt-6 font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl">
              <MaskedLine delay={0.25}>
                <span className="text-metallic">Managed IT services</span>
              </MaskedLine>
              <MaskedLine delay={0.4}>
                <span className="text-metallic">in {loc.city}.</span>
              </MaskedLine>
            </h1>
            <MaskedLine delay={0.55}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-[#C9D2DE]">{loc.intro}</p>
            </MaskedLine>
            <Reveal delay={0.65}>
              <div className="mt-8 inline-flex items-center gap-2 border border-brand/30 bg-brand/5 px-4 py-2.5" data-testid="onsite-badge">
                <MapPin size={16} className="text-brand" />
                <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-brand">
                  Home region — full on-site service included
                </span>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.3}>
              <div className="img-bluewash clip-edge relative mx-auto aspect-square w-full max-w-md overflow-hidden">
                <img src={loc.image} alt={loc.imageAlt} className="h-full w-full object-cover" loading="eager" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-panel/60" data-testid="city-areas">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-24">
          <Reveal>
            <p className="eyebrow">Where we work in {loc.city}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {loc.areas.map((a) => (
                <span key={a} className="glass px-5 py-2.5 font-display text-sm font-bold text-zinc-300" data-testid={`area-${a.toLowerCase().replace(/\s+/g, "-")}`}>
                  {a}
                </span>
              ))}
              <span className="px-5 py-2.5 text-sm text-[#A8B4C2]">…and everywhere in between</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="city-services">
        <Reveal>
          <p className="eyebrow">The full stack, in {loc.city}</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">
            <span className="text-metallic">Every service.</span> <span className="text-metallic">One standard.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={Math.min(i * 0.07, 0.35)}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`city-service-${s.slug}`}
                className="group glass flex h-full flex-col justify-between p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40"
              >
                <div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-white">{s.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#C9D2DE]">{s.short}</p>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-[#A8B4C2] transition-colors duration-300 group-hover:text-brand">
                  Details <ArrowRight size={14} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <VendorMarquee />

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="city-faq">
        <Reveal>
          <p className="eyebrow">Questions from {loc.city} businesses</p>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight md:text-5xl">
            <span className="text-metallic">Asked and answered.</span>
          </h2>
        </Reveal>
        <div className="mt-14 max-w-3xl space-y-5">
          {faqs.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.08}>
              <details className="group glass p-6 md:p-8" data-testid={`city-faq-${i}`}>
                <summary className="cursor-pointer list-none font-display text-lg font-bold text-white transition-colors duration-200 group-open:text-brand">
                  {f.q}
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-[#C9D2DE]">{f.a}</p>
              </details>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14">
            <p className="text-sm text-[#A8B4C2]">Also serving:</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {statePage && (
                <Link to={`/locations/${loc.stateSlug}`} className="text-sm text-[#C9D2DE] transition-colors duration-200 hover:text-brand" data-testid="city-back-to-state">
                  All of {loc.state}
                </Link>
              )}
              {CITIES.filter((c) => c.slug !== loc.slug).map((c) => (
                <Link key={c.slug} to={`/locations/${c.stateSlug}/${c.slug}`} className="text-sm text-[#C9D2DE] transition-colors duration-200 hover:text-brand" data-testid={`also-city-${c.slug}`}>
                  {c.city}, {c.stateAbbr}
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <CTABanner
        title={`Ready for better IT in ${loc.city}?`}
        sub="Book a free consultation. We'll look at your setup, tell you plainly what we'd change, and quote the Sempresera Standard for your team."
      />
    </>
  );
}
